import { ReactElement, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultActionMethods, WindowNames } from '@cashier/cashier-model';
import {
  convertToUrl,
  eventBus,
  EventFactory,
  ProviderWindowService,
} from '@cashier/cashier-shared';
import { Loader } from '@cashier/cashier-ui';

import { useHiddenFormContext } from '../../../contexts';
import { FallbackLink } from '../../fallbackLink';

import styles from './styles.module.scss';

export interface PaymentProgressProps {
  applyAction: (
    sendRequest: (
      method: ResultActionMethods,
      url: string,
      data: { [key: string]: string },
    ) => void,
    renderContent: (content: string) => void,
  ) => void;
  paymentMethodName: string;
  loadingPageUrl: string;
  isWebView: boolean;
  handleFallbackInitiated: (submitForm: () => void) => void;
}

export const PaymentProgress = ({
  applyAction,
  isWebView,
  loadingPageUrl,
  handleFallbackInitiated,
  paymentMethodName,
}: PaymentProgressProps): ReactElement => {
  const { t } = useTranslation();

  const { submitForm } = useHiddenFormContext();

  const sendRequest = useCallback(
    (
      method: ResultActionMethods,
      url: string,
      data: { [key: string]: string },
    ) => {
      if (ResultActionMethods.GET === method) {
        ProviderWindowService.redirect(convertToUrl(url, data));

        return;
      }

      if (isWebView) {
        eventBus.emit(EventFactory.submitForm(url, method.toString(), data));
      } else {
        ProviderWindowService.initialize(WindowNames.Window, loadingPageUrl);

        submitForm();
      }
    },
    [isWebView, loadingPageUrl, submitForm],
  );
  const renderContent = (output: string) => {
    ProviderWindowService.initialize(WindowNames.Window, 'about:blank');

    ProviderWindowService.writeContent(WindowNames.Window, output);
  };

  useEffect(() => {
    applyAction(sendRequest, renderContent);
  }, [applyAction, sendRequest]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.result}>
        <p className={styles.message} data-test="processing-message">
          {t('waitingFor.message', { paymentMethod: paymentMethodName })}
        </p>

        <div className={styles.loaderContainer} data-test="loader">
          <Loader theme="colored" size="small" />
        </div>
      </div>
      <FallbackLink
        version="v2"
        handleClick={() => handleFallbackInitiated(submitForm)}
      />
    </div>
  );
};
